import { useEffect, useState } from "react";

// The website discount code a visitor brought with them (a shared
// …/pricing?code=X link, or typed into the code box). It is kept until it is
// used or dismissed, so it survives login, sign-up, a password reset, a new
// tab, or wandering off to the home page. This module is the only owner of
// that storage; everything else reads and writes through it.
const KEY = "discount_code";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CHANGED = "tarbaa:discount-code";

// Same normalization as the server (funcs/payments/discounts.js).
export const normalizeCode = (raw) =>
  typeof raw === "string"
    ? raw.trim().toUpperCase().replace(/\s+/g, "").slice(0, 40)
    : "";

const notify = () => {
  try {
    window.dispatchEvent(new Event(CHANGED));
  } catch (_) {}
};

const write = (code) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch (_) {}
};

// Safe to call while rendering (never notifies).
export const getPendingDiscount = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const { code, at } = JSON.parse(raw);
      if (code && Date.now() - at < TTL_MS) return code;
      localStorage.removeItem(KEY);
    }
    // Older builds kept it per tab, as a bare string.
    const legacy = sessionStorage.getItem(KEY);
    if (legacy) {
      sessionStorage.removeItem(KEY);
      const code = normalizeCode(legacy);
      if (code) write(code);
      return code;
    }
  } catch (_) {}
  return "";
};

export const savePendingDiscount = (raw) => {
  const code = normalizeCode(raw);
  if (!code) return "";
  write(code);
  notify();
  return code;
};

export const clearPendingDiscount = () => {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch (_) {}
  notify();
};

export const pricingPath = (code) =>
  code ? `/pricing?code=${encodeURIComponent(code)}` : "/pricing";

// A shared link (…/pricing?code=X, /?code=X, /app…?code=X) leaves the code
// here before any redirect can drop the query string. No other page uses
// ?code= for anything else; game pages are left alone regardless.
const CAPTURE_ON = /^\/(pricing|app(\/.*)?|login|register)?$/;
export const captureDiscountFromUrl = (pathname, search) => {
  if (!CAPTURE_ON.test(pathname)) return;
  try {
    const code = new URLSearchParams(search).get("code");
    if (code) savePendingDiscount(code);
  } catch (_) {}
};

// The pending code, kept in sync across components and tabs.
export const usePendingDiscount = () => {
  const [code, setCode] = useState(getPendingDiscount);
  useEffect(() => {
    const sync = () => setCode(getPendingDiscount());
    window.addEventListener(CHANGED, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return code;
};
